import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import CSVMapping, { FieldMapping } from '~/types/CSVMapping';
import CSVValue from '~/types/CSVValue';
import * as csvFields from '~/utils/csvFields';

export type CSVState = {
  readonly isLoading: boolean;
  readonly fileUrl: Maybe<string>;
  readonly userValues: Maybe<readonly CSVValue[]>;
  readonly mapping: Maybe<Readonly<CSVMapping>>;
  readonly templates: readonly CSVMapping[];
  readonly name: Maybe<string>;
  readonly saveTemplateModalIsOpen: boolean;
  readonly deleteTemplateModalIsOpen: boolean;
  readonly errorFields: readonly number[];
  readonly errorText: Maybe<string>;
  readonly errorMessageShown: boolean;
  readonly saveTemplateSuccessModalIsOpen: boolean;
  readonly mappingHasChanged: boolean;
  readonly errorModalIsOpen: boolean;
  readonly errorModalMessage: Maybe<string>;
  readonly errorModalTitle: Maybe<string>;
};

const initialState: CSVState = {
  isLoading: false,
  fileUrl: null,
  userValues: null,
  mapping: null,
  templates: [],
  name: null,
  saveTemplateModalIsOpen: false,
  deleteTemplateModalIsOpen: false,
  errorFields: [],
  errorText: null,
  errorMessageShown: false,
  saveTemplateSuccessModalIsOpen: false,
  mappingHasChanged: false,
  errorModalIsOpen: false,
  errorModalMessage: null,
  errorModalTitle: null,
};

function csvImportReducer(state: CSVState = initialState, action: ActionTypes): CSVState {
  switch (action.type) {
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD_SUCCESS:
      return {
        ...state,
        isLoading: false,
        fileUrl: action.payload.fileUrl,
        userValues: action.payload.userValues,
        mapping: action.payload.mapping,
        templates: [action.payload.mapping],
        name: action.payload.mapping.name,
      };
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD_ERROR:
      return {
        ...state,
        isLoading: false,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
      };
    case ActionKeys.CSV_IMPORT_CHANGE_FIELD_MAPPING:
      let fieldMappingIndex = state.mapping!.fieldMappings.findIndex(
        (fieldMapping) => fieldMapping.colIdx === action.payload.index,
      );
      let newFieldMapping: Maybe<FieldMapping> = null;
      let startIndex: Maybe<number> = null;
      if (fieldMappingIndex === -1) {
        fieldMappingIndex = action.payload.index;
        newFieldMapping = {
          field: action.payload.field,
          colIdx: action.payload.index,
          colName: action.payload.colName,
        };
        startIndex = action.payload.index;
      } else {
        newFieldMapping = {
          ...state.mapping!.fieldMappings[fieldMappingIndex],
          field: action.payload.field,
        };
        startIndex = fieldMappingIndex + 1;
      }
      return {
        ...state,
        mappingHasChanged: true,
        mapping: {
          ...state.mapping!,
          fieldMappings: [
            ...state.mapping!.fieldMappings.slice(0, fieldMappingIndex),
            newFieldMapping,
            ...state.mapping!.fieldMappings.slice(startIndex),
          ],
        },
      };
    case ActionKeys.CSV_IMPORT_FETCH_TEMPLATES_SUCCESS:
      const newTemplates = action.payload.templates;
      return {
        ...state,
        templates: [state.mapping!, ...newTemplates],
      };
    case ActionKeys.CSV_IMPORT_FETCH_TEMPLATES_ERROR:
      return {
        ...state,
      };
    case ActionKeys.CSV_IMPORT_CHANGE_TEMPLATE:
      const newTemplate = state.templates.find((template) => template.id === action.value);
      const colIndexes = newTemplate!.fieldMappings.reduce<Record<number, FieldMapping>>(
        (hash, fieldMapping) => ({ ...hash, [fieldMapping.colIdx]: fieldMapping }),
        {},
      );
      const fieldMappings: FieldMapping[] = [];
      let index = 0;
      for (const userValue of state.userValues!) {
        fieldMappings[index] = !colIndexes[index]
          ? {
              field: csvFields.ignoreField,
              colName: userValue.colName,
              colIdx: index,
            }
          : {
              colName: userValue.colName,
              colIdx: index,
              field: colIndexes[index].field,
            };
        index += 1;
      }
      return {
        ...state,
        mapping: {
          ...newTemplate!,
          fieldMappings,
        },
        name: newTemplate!.name,
        mappingHasChanged: false,
      };
    case ActionKeys.CSV_IMPORT_SAVE_TEMPLATE_SUCCESS:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
        saveTemplateSuccessModalIsOpen: true,
      };
    case ActionKeys.CSV_IMPORT_SAVE_TEMPLATE_ERROR:
      return {
        ...state,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_MODAL_OPEN:
      return {
        ...state,
        deleteTemplateModalIsOpen: !state.deleteTemplateModalIsOpen,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_SUCCESS:
      const fetchedTemplates = action.payload.templates;
      const defaultMapping = state.templates.find((template) => template.name === 'Default');
      if (
        defaultMapping &&
        state.mapping &&
        action.payload.deletedTemplateId === state.mapping.id
      ) {
        return {
          ...state,
          mapping: defaultMapping,
          templates: [defaultMapping, ...fetchedTemplates],
          deleteTemplateModalIsOpen: false,
          isLoading: false,
        };
      }

      return {
        ...state,
        templates: [defaultMapping!, ...fetchedTemplates],
        deleteTemplateModalIsOpen: false,
        isLoading: false,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_ERROR:
      return {
        ...state,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
        isLoading: false,
      };
    case ActionKeys.CSV_IMPORT_OPEN_SAVE_CSV_TEMPLATE_MODAL:
      return {
        ...state,
        saveTemplateModalIsOpen: true,
        errorFields: [],
        errorText: null,
        errorMessageShown: false,
      };
    case ActionKeys.CSV_IMPORT_CLOSE_SAVE_CSV_TEMPLATE_MODAL:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
      };
    case ActionKeys.CSV_IMPORT_OPEN_ERROR_MESSAGE:
      return {
        ...state,
        errorText: action.payload.text,
        errorFields: action.payload.fields,
        errorMessageShown: true,
      };
    case ActionKeys.CSV_IMPORT_ORDERS_SUCCESS:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
        saveTemplateSuccessModalIsOpen: true,
      };
    case ActionKeys.CSV_IMPORT_ORDERS_ERROR:
      return {
        ...state,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
      };
    case ActionKeys.CSV_IMPORT_CLOSE_ERROR_MODAL:
      return {
        ...state,
        errorModalIsOpen: false,
      };
    case ActionKeys.CSV_CLEAN_STATE:
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

export default csvImportReducer;
